import { LitElement, css, html } from 'lit';
import { customElement, property } from 'lit/decorators';

import { openChatWindow } from '../../helpers/intercom';

export interface ServiceConfig {
    context: 'challenge' | 'competition';
    setupPrice: string;
    contextName: string;
    contextDescription: string;
}

@customElement('professional-services-component')
export class ProfessionalServicesComponent extends LitElement {
    @property({ type: Object })
    config: ServiceConfig = {
        context: 'challenge',
        setupPrice: '$250',
        contextName: 'challenge',
        contextDescription: 'challenges and maps'
    };

    @property({ type: String })
    amplitudeCallsite: string = '';

    render() {
        return html`
            <settings-card header="Professional Services">
                <p class="info-paragraph">Proxi offers professional services to help you set up and manage your ${this.config.contextDescription}. Contact us to learn more about our offerings.</p>
                
                <div class="service-item">
                    <h4>${this.config.context === 'competition' ? '🛠️ ' : ''}Custom Setup Support</h4>
                    <p>Get expert help setting up your ${this.config.contextName} ${this.config.context === 'competition' ? 'or voting campaign ' : 'or map '}with personalized guidance and configuration.</p>
                    <p><strong>Starting at ${this.config.setupPrice}</strong></p>
                    <form-button 
                        size="sm" 
                        variant="primary"
                        @click="${() => this.openServiceChat('custom-setup')}">
                        Get Quote
                    </form-button>
                </div>

                <div class="service-item">
                    <h4>${this.config.context === 'competition' ? '🏆 ' : ''}Trophies and Certificate Creation</h4>
                    <p>Professional design and distribution of physical trophies, digital certificates, and branded ${this.config.context === 'competition' ? 'awards for your competition winners' : 'rewards for your participants'}.</p>
                    <form-button 
                        size="sm" 
                        variant="primary"
                        @click="${() => this.openServiceChat('trophies')}">
                        Learn More
                    </form-button>
                </div>

                <div class="service-item">
                    <h4>${this.config.context === 'competition' ? '🔗 ' : ''}Custom Integrations</h4>
                    <p>Connect your ${this.config.contextName} with existing systems, APIs, or third-party platforms for seamless data flow${this.config.context === 'competition' ? ' and enhanced functionality' : ''}.</p>
                    <form-button 
                        size="sm" 
                        variant="primary"
                        @click="${() => this.openServiceChat('integrations')}">
                        Discuss Options
                    </form-button>
                </div>

                <div class="service-item">
                    <h4>${this.config.context === 'competition' ? '📢 ' : ''}Marketing and Promotion Support</h4>
                    <p>Strategic marketing guidance, promotional materials, and campaign management to maximize participation${this.config.context === 'competition' ? ' in your competition' : ''}.</p>
                    <form-button 
                        size="sm" 
                        variant="primary"
                        @click="${() => this.openServiceChat('marketing')}">
                        Get Started
                    </form-button>
                </div>

                <div class="service-item">
                    <h4>${this.config.context === 'competition' ? '📊 ' : ''}Data Analytics and Reporting</h4>
                    <p>Advanced analytics, custom reports, and insights to measure ${this.config.context === 'competition' ? 'competition success and improve future voting campaigns' : 'success and improve future challenges'}.</p>
                    <form-button 
                        size="sm" 
                        variant="primary"
                        @click="${() => this.openServiceChat('analytics')}">
                        View Options
                    </form-button>
                </div>
            </settings-card>
        `;
    }

    openServiceChat(serviceType: string) {
        let message = '';
        switch (serviceType) {
            case 'custom-setup':
                message = `I need to talk to a human about custom setup support for my ${this.config.contextName}. Can you connect me with someone who can provide a quote starting at ${this.config.setupPrice}?`;
                break;
            case 'trophies':
                message = `I need to talk to a human about trophies and certificate creation services${this.config.context === 'competition' ? ' for my competition winners' : ''}. Can you connect me with someone who can help with designing and distributing ${this.config.context === 'competition' ? 'awards' : 'rewards'}?`;
                break;
            case 'integrations':
                message = `I need to talk to a human about custom integrations for my Proxi ${this.config.contextName}. Can you connect me with someone who can discuss API and third-party platform connections?`;
                break;
            case 'marketing':
                message = `I need to talk to a human about marketing and promotion support for my ${this.config.contextName}. Can you connect me with someone who can help with campaign management?`;
                break;
            case 'analytics':
                message = `I need to talk to a human about data analytics and reporting services${this.config.context === 'competition' ? ' for my competition' : ' for my challenge'}. Can you connect me with someone who can provide advanced insights?`;
                break;
            default:
                message = `I need to talk to a human about Proxi professional services${this.config.context === 'competition' ? ' for my competition' : ''}. Can you connect me with someone from the team?`;
        }
        
        openChatWindow(this.amplitudeCallsite, message);
    }

    static get styles() {
        return css`
            .service-item {
                background-color: var(--theme-white-color);
                border: 1px solid var(--theme-border-color);
                border-radius: 0.5rem;
                padding: 1rem;
                margin-bottom: 1rem;
                transition: box-shadow 0.2s ease;
            }

            .service-item:hover {
                box-shadow: 0px 4px 12px 0px rgba(0, 0, 0, 0.1);
            }

            .service-item h4 {
                margin: 0 0 0.5rem 0;
                color: var(--main-primary-color);
                font-size: 1.1rem;
                font-weight: 600;
            }

            .service-item p {
                margin: 0 0 0.75rem 0;
                line-height: 1.4;
                color: var(--theme-text-color);
            }

            .service-item form-button {
                margin-top: 0.5rem;
            }

            .info-paragraph {
                color: var(--theme-input-color);
                margin-top: 0;
                margin-bottom: 0;
            }
        `;
    }
}